import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { on } from "@ember/modifier";
import { fn, array } from "@ember/helper";
import { service } from "@ember/service";
import eq from "truth-helpers/helpers/eq";
import not from "truth-helpers/helpers/not";
import and from "truth-helpers/helpers/and";
import or from "truth-helpers/helpers/or";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { debounce } from "@ember/runloop";
import KeyValueBuilder from "./key-value-builder";

const includes = (array, item) => array && array.includes(item);

const PROXY_TYPE_OPTIONS = ["residential", "datacenter", "mobile", "isp", "rotating"];

export default class AdminProviderManager extends Component {
  @service providerAdmin;
  @service dialog;

  @tracked providers = [];
  @tracked totalCount = 0;
  @tracked page = 1;
  @tracked limit = 15;
  @tracked searchQuery = "";
  @tracked proxyTypeFilter = "";
  @tracked statusFilter = "";
  @tracked loading = false;

  // Stats from the endpoint
  @tracked stats = {
    total_providers: 0,
    active_vendors: 0,
    avg_rating: 0,
    providers_needing_review: 0
  };

  // Selected provider details for editing
  @tracked selectedProvider = null;
  @tracked activeEditTab = "core"; // core, pricing, performance, reviews, affiliate, analytics
  @tracked editingProviderData = {};
  @tracked editingProfileData = {};
  @tracked saving = false;
  @tracked formDirty = false; // tracks unsaved changes in the edit drawer
  @tracked saveSuccess = false; // brief "Saved ✓" feedback after save
  @tracked successMessage = ""; // floating confirmation banner
  @tracked fieldErrors = {}; // field-keyed validation errors for inline display

  showToast(msg) {
    this.successMessage = msg;
    setTimeout(() => {
      this.successMessage = "";
    }, 4000);
  }

  // Cross-plugin summary for selected provider
  @tracked selectedProviderSummary = null;
  @tracked summaryLoading = false;

  // Reviews moderation for selected provider
  @tracked providerReviews = [];
  @tracked reviewsLoading = false;
  @tracked reviewsQueue = "all"; // all, pending, published, reported, hidden

  // Bulk selection
  @tracked selectedProviderIds = [];

  // Per-row loading state for inline edits (prevents double-clicks)
  @tracked inlineSavingId = null;

  // Collapsible split-pane: when a provider is selected for editing, the
  // left list pane collapses to a narrow strip so the edit form gets full
  // width. Toggle manually with the collapse button.
  @tracked paneCollapsed = false;

  // Accordion state for the Core Info tab — groups the 18 fields into
  // 4 collapsible sections to reduce visual overwhelm. Identity and
  // Classification are expanded by default; Editorial Profile (the
  // heaviest) is collapsed.
  @tracked coreSections = {
    identity: true,
    classification: true,
    featured: false,
    metadata: false,
    editorial: false,
  };

  @action
  toggleCoreSection(section) {
    this.coreSections = { ...this.coreSections, [section]: !this.coreSections[section] };
  }

  // CSV Import state
  @tracked showImportForm = false;
  @tracked importPreview = null;
  @tracked importErrors = [];
  @tracked importToken = null;
  @tracked importing = false;

  // New provider creation state — multi-step modal
  @tracked showCreateModal = false;
  @tracked createStep = 1;
  @tracked createData = {};
  @tracked createTypePricings = [];
  @tracked sortBy = "name";
  @tracked sortOrder = "asc";
  @tracked viewMode = "table";
  @tracked isExpanded = false;

  @action
  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  // Per-type pricing overrides loaded when a provider is selected for editing.
  // Shape: [{ proxy_type, price_per_gb }] — one row per offered type.
  @tracked providerTypePricings = [];
  proxyTypeOptions = PROXY_TYPE_OPTIONS;

  @action
  setProperty(property, event) {
    this[property] = event.target.value;
  }

  // Prevent row-click (selectProvider) from firing when interacting with
  // inline controls (checkbox, status select, action buttons).
  @action
  stopRowClick(e) {
    e.stopPropagation();
  }

  // Clicking the dark backdrop behind the edit pane closes it (with
  // dirty-state confirmation, same as the × button).
  @action
  handleOverlayClick() {
    this.closeDrawer();
  }

  // Hide broken logo images and show the letter-fallback placeholder instead.
  @action
  handleLogoError(e) {
    e.target.style.display = "none";
    const fallback = e.target.parentElement.querySelector(".logo-placeholder");
    if (fallback) {
      fallback.style.display = "flex";
    }
  }

  logoInitial(name) {
    return (name || "?").charAt(0).toUpperCase();
  }

  @action
  setChecked(property, event) {
    this[property] = event.target.checked;
  }

  @action
  closeDrawer() {
    // Guard against accidental data loss — if the admin has edited fields
    // without saving, confirm before discarding.
    if (this.formDirty) {
      this.dialog.yesNoConfirm({
        message: "You have unsaved changes. Discard them and close?",
        didConfirm: () => {
          this.formDirty = false;
          this.selectedProvider = null;
        },
      });
      return;
    }
    this.selectedProvider = null;
  }

  @action
  togglePaneCollapse() {
    this.paneCollapsed = !this.paneCollapsed;
  }

  @action
  markDirty() {
    this.formDirty = true;
    this.saveSuccess = false;
    this.fieldErrors = {}; // clear inline errors on any edit
  }

  constructor() {
    super(...arguments);
    this.loadProviders();
    this._keydownHandler = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this._keydownHandler);
  }

  get totalPages() {
    return Math.ceil(this.totalCount / this.limit) || 1;
  }

  get hasPrevPage() {
    return this.page > 1;
  }

  get hasNextPage() {
    return this.page < this.totalPages;
  }

  get allSelected() {
    return this.providers.length > 0 && this.selectedProviderIds.length === this.providers.length;
  }

  @action
  async loadProviders() {
    this.loading = true;
    try {
      const offset = (this.page - 1) * this.limit;
      const res = await this.providerAdmin.loadProviders({
        search: this.searchQuery,
        proxy_type: this.proxyTypeFilter,
        status: this.statusFilter,
        sort: this.sortBy,
        order: this.sortOrder,
        limit: this.limit,
        offset: offset
      });
      this.providers = (res.providers || []).map(p => p.provider || p);
      this.totalCount = res.total || 0;

      // Do NOT auto-select the first provider — the modal should only open
      // when the admin clicks a row or the Edit button, not on page load.

      // Update directory stats based on fetched response metadata if provided, otherwise compute from local list
      if (res.stats) {
        this.stats = res.stats;
      } else {
        this.loadGlobalStats();
      }
    } catch (err) {
      popupAjaxError(err);
    } finally {
      this.loading = false;
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    if (this._keydownHandler) {
      document.removeEventListener("keydown", this._keydownHandler);
    }
  }

  @action
  handleKeydown(e) {
    // ESC closes the edit modal (even when focused inside an input).
    if (e.key === 'Escape' && this.selectedProvider) {
      e.preventDefault();
      this.closeDrawer();
      return;
    }

    // Arrow navigation only when not typing in a field.
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
      return;
    }
    if (!this.providers || this.providers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.selectNextProvider();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.selectPreviousProvider();
    }
  }

  @action
  selectNextProvider() {
    if (!this.selectedProvider) {
      this.selectProvider(this.providers[0]);
      return;
    }
    const idx = this.providers.findIndex(p => p.id === this.selectedProvider.id);
    if (idx < this.providers.length - 1) {
      this.selectProvider(this.providers[idx + 1]);
    }
  }

  @action
  selectPreviousProvider() {
    if (!this.selectedProvider) {
      this.selectProvider(this.providers[0]);
      return;
    }
    const idx = this.providers.findIndex(p => p.id === this.selectedProvider.id);
    if (idx > 0) {
      this.selectProvider(this.providers[idx - 1]);
    }
  }

  get currentIndex() {
    if (!this.selectedProvider) return -1;
    return this.providers.findIndex(p => p.id === this.selectedProvider.id);
  }

  get canGoPrev() {
    return this.currentIndex <= 0;
  }

  get canGoNext() {
    return this.currentIndex < 0 || this.currentIndex >= this.providers.length - 1;
  }

  // Prev/Next with dirty-state guard — confirm before discarding unsaved edits
  @action
  prevProvider() {
    if (this.canGoPrev) return;
    const doSwitch = () => {
      this.formDirty = false;
      this.selectPreviousProvider();
    };
    if (this.formDirty) {
      this.dialog.yesNoConfirm({
        message: "You have unsaved changes. Discard them and switch provider?",
        didConfirm: doSwitch,
      });
    } else {
      doSwitch();
    }
  }

  @action
  nextProvider() {
    if (this.canGoNext) return;
    const doSwitch = () => {
      this.formDirty = false;
      this.selectNextProvider();
    };
    if (this.formDirty) {
      this.dialog.yesNoConfirm({
        message: "You have unsaved changes. Discard them and switch provider?",
        didConfirm: doSwitch,
      });
    } else {
      doSwitch();
    }
  }

  async loadGlobalStats() {
    try {
      const res = await ajax("/admin/plugins/proxyviews-directory/dashboard/stats");
      if (res) {
        this.stats = res;
      }
    } catch (err) {
      // Non-blocking
    }
  }

  @action
  setPage(page) {
    if (page >= 1 && page <= this.totalPages) {
      this.page = page;
      this.loadProviders();
    }
  }

  @action
  prevPage() {
    this.setPage(this.page - 1);
  }

  @action
  nextPage() {
    this.setPage(this.page + 1);
  }

  @action
  updateSearch(e) {
    this.searchQuery = e.target.value;
    this.page = 1;
    // Debounce to avoid firing a network request per keystroke on large
    // directories. The sibling directory controller uses the same pattern
    // with a 250ms delay.
    debounce(this, this.loadProviders, 250);
  }

  @action
  changeProxyTypeFilter(e) {
    this.proxyTypeFilter = e.target.value;
    this.page = 1;
    this.loadProviders();
  }

  @action
  changeStatusFilter(e) {
    this.statusFilter = e.target.value;
    this.page = 1;
    this.loadProviders();
  }

  @action
  toggleSelectAll(e) {
    if (e.target.checked) {
      this.selectedProviderIds = this.providers.map(p => p.id);
    } else {
      this.selectedProviderIds = [];
    }
  }

  @action
  toggleSelectProvider(providerId, e) {
    if (e.target.checked) {
      if (!this.selectedProviderIds.includes(providerId)) {
        this.selectedProviderIds = [...this.selectedProviderIds, providerId];
      }
    } else {
      this.selectedProviderIds = this.selectedProviderIds.filter(id => id !== providerId);
    }
  }

  @action
  applyBulkStatus(status) {
    if (!this.selectedProviderIds.length) return;
    
    this.dialog.yesNoConfirm({
      message: `Are you sure you want to change status to '${status}' for ${this.selectedProviderIds.length} providers?`,
      didConfirm: async () => {
        try {
          await this.providerAdmin.bulkUpdate(this.selectedProviderIds, status, null);
          this.selectedProviderIds = [];
          this.loadProviders();
        } catch (err) {
          popupAjaxError(err);
        }
      }
    });
  }

  @action
  applyBulkVerified(verified) {
    if (!this.selectedProviderIds.length) return;

    this.dialog.yesNoConfirm({
      message: `Are you sure you want to set Verified to ${verified} for ${this.selectedProviderIds.length} providers?`,
      didConfirm: async () => {
        try {
          await this.providerAdmin.bulkUpdate(this.selectedProviderIds, null, verified);
          this.selectedProviderIds = [];
          this.loadProviders();
        } catch (err) {
          const json = err.jqXHR?.responseJSON || {};
          const failed = json.errors?.length || 0;
          if (failed > 0) {
            this.dialog.alert(`Bulk update partially failed: ${failed} provider(s) could not be updated.`);
          } else {
            popupAjaxError(err);
          }
          this.loadProviders();
        }
      }
    });
  }

  @action
  bulkDelete() {
    if (!this.selectedProviderIds.length) return;
    const count = this.selectedProviderIds.length;

    this.dialog.yesNoConfirm({
      message: `Are you sure you want to PERMANENTLY DELETE ${count} provider(s)? This cannot be undone.`,
      didConfirm: async () => {
        this.saving = true;
        try {
          const res = await ajax("/admin/plugins/proxyviews-directory/networks/bulk-destroy", {
            type: "POST",
            data: { provider_ids: this.selectedProviderIds },
          });
          this.selectedProviderIds = [];
          this.selectedProvider = null;
          this.showToast(`${count} provider(s) deleted successfully!`);
          this.loadProviders();
          if (res.destroyed_count < count) {
            this.dialog.alert(`Deleted ${res.destroyed_count} of ${count} providers. Some could not be deleted.`);
          }
        } catch (err) {
          popupAjaxError(err);
        } finally {
          this.saving = false;
        }
      }
    });
  }

  @action
  toggleNewForm() {
    this.openCreateModal();
  }

  @action
  openCreateModal() {
    const blank = {
      isNew: true,
      name: "",
      slug: "new-provider",
      website_url: "",
      logo_url: "",
      description: "",
      proxy_type: "residential",
      proxy_types: ["residential"],
      status: "pending",
      verified: false,
      real_cost_per_gb: 0,
      uptime_percentage: 100,
      ip_pool_size: 0,
      benchmark_score: 0,
      metadata: {
        free_trial: "",
        speed: "",
        locations: "",
        deal: "",
        coupon_code: "",
        pay_as_you_go: false
      }
    };
    this.selectProvider(blank);
  }

  @action
  closeCreateModal() {
    this.showCreateModal = false;
    this.createStep = 1;
  }

  @action
  nextCreateStep() {
    if (!this.createData.name?.trim()) {
      this.dialog.alert("Provider name is required");
      return;
    }
    this.createStep = 2;
  }

  @action
  prevCreateStep() {
    this.createStep = 1;
  }

  @action
  updateCreateField(field, event) {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    this.createData = { ...this.createData, [field]: value };
  }

  @action
  toggleCreateType(type) {
    const current = this.createData.proxy_types || [];
    this.createData = {
      ...this.createData,
      proxy_types: current.includes(type) ? current.filter(t => t !== type) : [...current, type]
    };
    // Auto-add/remove pricing row when type is toggled
    if (current.includes(type)) {
      this.createTypePricings = this.createTypePricings.filter(p => p.proxy_type !== type);
    } else {
      this.createTypePricings = [...this.createTypePricings, { proxy_type: type, price_per_gb: null }];
    }
  }

  @action
  updateCreateTypePricing(type, event) {
    const price = parseFloat(event.target.value);
    const normalized = isNaN(price) ? null : price;
    const existing = this.createTypePricings.find(p => p.proxy_type === type);
    if (existing) {
      this.createTypePricings = this.createTypePricings.map(p =>
        p.proxy_type === type ? { ...p, price_per_gb: normalized } : p
      );
    } else {
      this.createTypePricings = [...this.createTypePricings, { proxy_type: type, price_per_gb: normalized }];
    }
  }

  createPriceForType(type) {
    if (!this.createTypePricings) return null;
    const row = this.createTypePricings.find(p => p.proxy_type === type);
    return row ? row.price_per_gb : null;
  }

  @action
  async createProvider(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!this.createData.name?.trim()) {
      this.dialog.alert("Provider name is required");
      return;
    }
    this.saving = true;
    try {
      const payload = {
        name: this.createData.name,
        website_url: this.createData.website_url || null,
        logo_url: this.createData.logo_url || null,
        description: this.createData.description || null,
        proxy_types: this.createData.proxy_types,
        status: this.createData.status,
        verified: this.createData.verified,
      };
      // Optional step-2 fields
      if (this.createData.real_cost_per_gb) payload.real_cost_per_gb = parseFloat(this.createData.real_cost_per_gb) || 0;
      if (this.createData.uptime_percentage) payload.uptime_percentage = parseFloat(this.createData.uptime_percentage) || 0;
      if (this.createData.ip_pool_size) payload.ip_pool_size = parseInt(this.createData.ip_pool_size, 10) || 0;
      if (this.createData.benchmark_score) payload.benchmark_score = parseFloat(this.createData.benchmark_score) || 0;

      const result = await this.providerAdmin.createProvider(payload);
      const newId = result?.provider?.id;

      // Save per-type pricing overrides if any were set
      if (newId && this.createTypePricings.length > 0) {
        const priced = this.createTypePricings.filter(p => p.price_per_gb !== null);
        if (priced.length > 0) {
          try {
            await ajax(`/admin/plugins/proxyviews-directory/networks/${newId}/pricings.json`, {
              type: "PUT",
              data: { pricings: priced },
            });
          } catch (err) {
            console.warn("Per-type pricing save failed after create:", err);
          }
        }
      }

      this.showCreateModal = false;
      this.createStep = 1;
      this.loadProviders();
    } catch (err) {
      this.fieldErrors = this.extractErrors(err);
    } finally {
      this.saving = false;
    }
  }

  @action
  sortByColumn(column) {
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
    } else {
      this.sortBy = column;
      this.sortOrder = "asc";
    }
    this.page = 1;
    this.loadProviders();
  }

  get sortIndicator() {
    return this.sortOrder === "asc" ? "▲" : "▼";
  }

  get formattedFeaturedUntil() {
    const val = this.editingProviderData?.featured_until;
    if (!val) return "";
    return typeof val === "string" ? val.slice(0, 10) : "";
  }

  @action
  toggleViewMode() {
    this.viewMode = this.viewMode === "table" ? "grid" : "table";
  }

  @action
  filterByStat(filterType) {
    if (filterType === "active") {
      this.statusFilter = "active";
    } else if (filterType === "pending") {
      this.statusFilter = "pending";
    } else {
      this.statusFilter = "";
    }
    this.proxyTypeFilter = "";
    this.page = 1;
    this.loadProviders();
  }

  @action
  saveInlineField(provider, field, event) {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    if (provider[field] === value) return;

    // Confirm before transitioning to destructive statuses
    if (field === "status" && ["suspended", "archived"].includes(value)) {
      this.dialog.yesNoConfirm({
        message: `Change "${provider.name}" status to "${value}"?`,
        didConfirm: () => this._executeInlineSave(provider, field, value),
        didCancel: () => { this.loadProviders(); }, // revert the select
      });
      return;
    }

    this._executeInlineSave(provider, field, value);
  }

  async _executeInlineSave(provider, field, value) {
    this.inlineSavingId = provider.id;

    // Optimistic update
    this.providers = this.providers.map(p => p.id === provider.id ? { ...p, [field]: value } : p);

    try {
      await this.providerAdmin.saveProvider(provider.id, { [field]: value });
    } catch (err) {
      popupAjaxError(err);
      this.loadProviders(); // Revert
    } finally {
      this.inlineSavingId = null;
    }
  }

  @action
  async selectProvider(provider) {
    this.selectedProvider = provider;
    this.activeEditTab = "core";
    this.formDirty = false;
    this.saveSuccess = false;
    this.editingProviderData = { ...provider };

    // Ensure metadata is a full object with all known fields
    this.editingProviderData.metadata = {
      free_trial: "",
      speed: "",
      locations: "",
      deal: "",
      coupon_code: "",
      pay_as_you_go: false,
      ...(provider.metadata || {})
    };

    // Parse helper arrays/objects for editing inputs
    this.editingProviderData.supported_protocols = (provider.supported_protocols || []).join(", ");
    this.editingProviderData.country_codes = (provider.country_codes || []).join(", ");
    this.editingProviderData.use_cases = (provider.use_cases || []).join(", ");
    this.editingProviderData.pricing_tiers_json = JSON.stringify(provider.pricing_tiers || {}, null, 2);
    // Ensure proxy_types is an array (fall back to legacy scalar)
    this.editingProviderData.proxy_types = provider.proxy_types || [provider.proxy_type].filter(Boolean);

    // Reset per-type pricing until loaded below
    this.providerTypePricings = [];

    this.editingProfileData = {};

    if (provider.isNew) {
      return;
    }

    // Load full provider profile & content
    try {
      const detail = await this.providerAdmin.loadProvider(provider.id);
      if (detail && detail.content) {
        this.editingProviderData.description = detail.content.description || "";
        this.editingProviderData.supported_protocols = (detail.content.supported_protocols || []).join(", ");
        this.editingProviderData.country_codes = (detail.content.country_codes || []).join(", ");
        this.editingProviderData.use_cases = (detail.content.use_cases || []).join(", ");
        this.editingProviderData.pricing_tiers_json = JSON.stringify(detail.content.pricing_tiers || {}, null, 2);
        this.editingProviderData.ping_endpoints_json = JSON.stringify(detail.content.ping_endpoints || {}, null, 2);
        this.editingProviderData.ip_pool_size = detail.content.ip_pool_size;
        this.editingProviderData.asn_diversity_score = detail.content.asn_diversity_score;
        this.editingProviderData.uptime_percentage = detail.content.uptime_percentage;
        this.editingProviderData.benchmark_score = detail.content.benchmark_score;
        this.editingProviderData.verified_block_rate = detail.content.verified_block_rate;
        this.editingProviderData.avg_session_affinity_length = detail.content.avg_session_affinity_length;
        this.editingProviderData.verification_tier = detail.content.verification_tier || "none";
      }
      if (detail && detail.profile) {
        this.editingProfileData = { ...detail.profile };
        this.editingProfileData.pros = (detail.profile.pros || []).join("\n");
        this.editingProfileData.cons = (detail.profile.cons || []).join("\n");
        this.editingProfileData.faq_json = JSON.stringify(detail.profile.faq || [], null, 2);
      }
    } catch (err) {
      popupAjaxError(err);
    }

    // Load cross-plugin summary data
    this.loadProviderSummary(provider.id);

    // Load per-type pricing overrides
    this.loadProviderPricings(provider.id);
  }

  @action
  toggleProxyType(type) {
    this.formDirty = true;
    const current = Array.isArray(this.editingProviderData.proxy_types)
      ? this.editingProviderData.proxy_types
      : [this.editingProviderData.proxy_type].filter(Boolean);
    let nextTypes;
    if (current.includes(type)) {
      nextTypes = current.filter((t) => t !== type);
    } else {
      nextTypes = [...current, type];
    }
    this.editingProviderData = {
      ...this.editingProviderData,
      proxy_types: nextTypes,
    };
  }

  @action
  toggleNewProviderType(type) {
    if (this.newProviderTypes.includes(type)) {
      this.newProviderTypes = this.newProviderTypes.filter((t) => t !== type);
    } else {
      this.newProviderTypes = [...this.newProviderTypes, type];
    }
  }

  async loadProviderPricings(providerId) {
    try {
      const data = await ajax(`/admin/plugins/proxyviews-directory/networks/${providerId}/pricings`);
      this.providerTypePricings = data.pricings || [];
    } catch {
      // Pricing endpoint may 404 if migrations haven't run — non-fatal.
      this.providerTypePricings = [];
    }
  }

  @action
  priceForType(type) {
    const row = this.providerTypePricings.find((p) => p.proxy_type === type);
    return row ? row.price_per_gb : null;
  }

  @action
  pricingUnitForType(type) {
    const row = this.providerTypePricings.find((p) => p.proxy_type === type);
    return row ? row.pricing_unit : "per_gb";
  }

  @action
  updatePricingUnit(type, event) {
    const unit = event.target.value;
    const existing = this.providerTypePricings.find((p) => p.proxy_type === type);
    if (existing) {
      this.providerTypePricings = this.providerTypePricings.map((p) =>
        p.proxy_type === type ? { ...p, pricing_unit: unit } : p
      );
    }
    this.formDirty = true;
  }

  @action
  updateTypePricing(type, event) {
    const price = parseFloat(event.target.value);
    const normalized = isNaN(price) ? null : price;
    const existing = this.providerTypePricings.find((p) => p.proxy_type === type);
    if (existing) {
      // Replace the whole array with updated object so @tracked fires
      this.providerTypePricings = this.providerTypePricings.map((p) =>
        p.proxy_type === type ? { ...p, price_per_gb: normalized } : p
      );
    } else {
      this.providerTypePricings = [
        ...this.providerTypePricings,
        { proxy_type: type, price_per_gb: normalized },
      ];
    }
    this.formDirty = true;
  }

  @action
  changeEditTab(tab) {
    this.activeEditTab = tab;
    if (tab === "reviews") {
      this.loadProviderReviews();
    }
  }

  async loadProviderSummary(providerId) {
    this.summaryLoading = true;
    try {
      this.selectedProviderSummary = await this.providerAdmin.loadSummary(providerId);
    } catch (err) {
      // Non-blocking
    } finally {
      this.summaryLoading = false;
    }
  }

  @action
  changeReviewsQueue(e) {
    this.reviewsQueue = e.target.value;
    this.loadProviderReviews();
  }

  async loadProviderReviews() {
    if (!this.selectedProvider) return;
    this.reviewsLoading = true;
    try {
      const res = await ajax("/admin/plugins/proxyviews-reviews/moderation", {
        data: {
          provider_id: this.selectedProvider.id,
          queue: this.reviewsQueue
        }
      });
      this.providerReviews = res.reviews || [];
    } catch (err) {
      // Non-blocking
    } finally {
      this.reviewsLoading = false;
    }
  }

  @action
  async moderateReview(reviewId, actionType) {
    try {
      await ajax(`/admin/plugins/proxyviews-reviews/moderation/${reviewId}/${actionType}`, {
        type: "POST"
      });
      this.loadProviderReviews();
      this.loadProviderSummary(this.selectedProvider.id);
    } catch (err) {
      popupAjaxError(err);
    }
  }

  @action
  async toggleReviewVerifiedPurchase(reviewId, currentVal) {
    try {
      await ajax(`/admin/plugins/proxyviews-reviews/moderation/${reviewId}/verified-purchase`, {
        type: "PUT",
        data: { verified_purchase: !currentVal }
      });
      this.loadProviderReviews();
    } catch (err) {
      popupAjaxError(err);
    }
  }

  @action
  updateEditField(field, e) {
    this.markDirty();
    let value;
    if (e && e.target) {
      value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    } else {
      value = e;
    }
    this.editingProviderData = {
      ...this.editingProviderData,
      [field]: value
    };
  }

  @action
  updateMetadataField(field, e) {
    this.editingProviderData = {
      ...this.editingProviderData,
      metadata: {
        ...(this.editingProviderData.metadata || {}),
        [field]: e.target.value
      }
    };
    this.markDirty();
  }

  @action
  updateMetadataCheckbox(field, e) {
    this.editingProviderData = {
      ...this.editingProviderData,
      metadata: {
        ...(this.editingProviderData.metadata || {}),
        [field]: e.target.checked
      }
    };
    this.markDirty();
  }

  @action
  updateProfileField(field, e) {
    this.markDirty();
    const value = (e && e.target) ? e.target.value : e;
    this.editingProfileData = {
      ...this.editingProfileData,
      [field]: value
    };
  }

  // Client-side validation — checks common issues before hitting the server.
  // Returns { valid, errors } where errors is field-keyed.
  validateProvider(data) {
    const errors = {};
    if (!data.name || !data.name.trim()) errors.name = ["Name is required"];

    if (data.website_url && data.website_url.trim() !== "") {
      let url = data.website_url.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
        data.website_url = url;
      }
    }

    if (data.logo_url && data.logo_url.trim() !== "") {
      let url = data.logo_url.trim();
      if (!/^https?:\/\//i.test(url) && !/^\//.test(url)) {
        url = "https://" + url;
        data.logo_url = url;
      }
    }

    return { valid: Object.keys(errors).length === 0, errors };
  }

  // Extract structured errors from an AJAX error response.
  extractErrors(err) {
    const json = err.jqXHR?.responseJSON || {};
    // Prefer field-keyed errors; fall back to flat messages
    if (json.errors && typeof json.errors === "object" && !Array.isArray(json.errors)) {
      return json.errors;
    }
    if (json.error_messages) {
      return { base: json.error_messages };
    }
    if (json.errors && Array.isArray(json.errors)) {
      return { base: json.errors };
    }
    return { base: ["An unexpected error occurred"] };
  }

  // Called by the footer Save button (which lives outside the <form>).
  // Triggers the same save logic as the form submit.
  @action
  submitForm(e) {
    if (e && e.preventDefault) e.preventDefault();
    this.saveProvider(e);
  }

  @action
  async saveProvider(e) {
    if (e && e.preventDefault) e.preventDefault();

    // Client-side validation
    this.fieldErrors = {};
    const { valid, errors } = this.validateProvider(this.editingProviderData);
    if (!valid) {
      this.fieldErrors = errors;
      return;
    }

    this.saving = true;
    const id = this.selectedProvider.id;

    try {
      // Parse arrays/JSON safely before saving
      const protocols = typeof this.editingProviderData.supported_protocols === "string"
        ? this.editingProviderData.supported_protocols.split(",").map(p => p.trim()).filter(Boolean)
        : (Array.isArray(this.editingProviderData.supported_protocols) ? this.editingProviderData.supported_protocols : []);

      const countries = typeof this.editingProviderData.country_codes === "string"
        ? this.editingProviderData.country_codes.split(",").map(c => c.trim().toUpperCase()).filter(Boolean)
        : (Array.isArray(this.editingProviderData.country_codes) ? this.editingProviderData.country_codes : []);

      const cases = typeof this.editingProviderData.use_cases === "string"
        ? this.editingProviderData.use_cases.split(",").map(u => u.trim()).filter(Boolean)
        : (Array.isArray(this.editingProviderData.use_cases) ? this.editingProviderData.use_cases : []);

      let pricingTiers = {};
      if (typeof this.editingProviderData.pricing_tiers_json === "object" && this.editingProviderData.pricing_tiers_json !== null) {
        pricingTiers = this.editingProviderData.pricing_tiers_json;
      } else if (typeof this.editingProviderData.pricing_tiers_json === "string" && this.editingProviderData.pricing_tiers_json.trim()) {
        try {
          pricingTiers = JSON.parse(this.editingProviderData.pricing_tiers_json);
        } catch (err) {
          this.dialog.alert("Invalid Pricing Tiers JSON format");
          this.saving = false;
          return;
        }
      }

      let pingEndpoints = {};
      if (typeof this.editingProviderData.ping_endpoints_json === "object" && this.editingProviderData.ping_endpoints_json !== null) {
        pingEndpoints = this.editingProviderData.ping_endpoints_json;
      } else if (typeof this.editingProviderData.ping_endpoints_json === "string" && this.editingProviderData.ping_endpoints_json.trim()) {
        try {
          pingEndpoints = JSON.parse(this.editingProviderData.ping_endpoints_json);
        } catch (err) {
          this.dialog.alert("Invalid Ping Endpoints JSON format");
          this.saving = false;
          return;
        }
      }

      let faq = [];
      if (Array.isArray(this.editingProfileData.faq_json)) {
        faq = this.editingProfileData.faq_json;
      } else if (typeof this.editingProfileData.faq_json === "string" && this.editingProfileData.faq_json.trim()) {
        try {
          faq = JSON.parse(this.editingProfileData.faq_json);
        } catch (err) {
          this.dialog.alert("Invalid FAQ JSON format");
          this.saving = false;
          return;
        }
      }

      const pros = typeof this.editingProfileData.pros === "string"
        ? this.editingProfileData.pros.split("\n").map(p => p.trim()).filter(Boolean)
        : (Array.isArray(this.editingProfileData.pros) ? this.editingProfileData.pros : []);

      const cons = typeof this.editingProfileData.cons === "string"
        ? this.editingProfileData.cons.split("\n").map(c => c.trim()).filter(Boolean)
        : (Array.isArray(this.editingProfileData.cons) ? this.editingProfileData.cons : []);

      const selectedTypes = (Array.isArray(this.editingProviderData.proxy_types) && this.editingProviderData.proxy_types.length > 0)
        ? this.editingProviderData.proxy_types
        : ["residential"];

      if (this.selectedProvider && this.selectedProvider.isNew) {
        if (!this.editingProviderData.name?.trim()) {
          this.dialog.alert("Provider name is required");
          this.saving = false;
          return;
        }

        const createRes = await this.providerAdmin.createProvider({
          name: this.editingProviderData.name,
          website_url: this.editingProviderData.website_url || null,
          logo_url: this.editingProviderData.logo_url || null,
          description: this.editingProviderData.description || null,
          proxy_type: selectedTypes[0] || "residential",
          proxy_types: selectedTypes,
          status: this.editingProviderData.status || "pending",
          verified: !!this.editingProviderData.verified,
          real_cost_per_gb: parseFloat(this.editingProviderData.real_cost_per_gb) || 0.0,
          uptime_percentage: parseInt(this.editingProviderData.uptime_percentage, 10) || 0,
          ip_pool_size: parseInt(this.editingProviderData.ip_pool_size, 10) || 0,
          benchmark_score: parseFloat(this.editingProviderData.benchmark_score) || 0.0,
          verified_block_rate: parseFloat(this.editingProviderData.verified_block_rate) || 0.0,
          avg_session_affinity_length: parseFloat(this.editingProviderData.avg_session_affinity_length) || 0.0,
          verification_tier: this.editingProviderData.verification_tier || "none",
          featured_weight: parseInt(this.editingProviderData.featured_weight, 10) || 0,
          featured_rank: parseInt(this.editingProviderData.featured_rank, 10) || 0,
          featured_until: this.editingProviderData.featured_until || null,
          provider_of_month: !!this.editingProviderData.provider_of_month,
          supported_protocols: protocols,
          country_codes: countries,
          use_cases: cases,
          pricing_tiers: pricingTiers,
          ping_endpoints: pingEndpoints,
          metadata: this.editingProviderData.metadata || {},
          profile_attributes: {
            overview: this.editingProfileData.overview || "",
            what_works: this.editingProfileData.what_works || "",
            what_hurts: this.editingProfileData.what_hurts || "",
            setup_guide: this.editingProfileData.setup_guide || "",
            sidebar_cta_text: this.editingProfileData.sidebar_cta_text || "",
            affiliate_url: this.editingProfileData.affiliate_url || "",
            pros: pros,
            cons: cons,
            faq: faq
          }
        });

        const created = createRes?.provider;
        if (!created || !created.id) {
          this.dialog.alert("Failed to create provider");
          this.saving = false;
          return;
        }

        this.selectedProvider = { ...created, isNew: false };
        this.editingProviderData = {
          ...this.editingProviderData,
          ...created,
          proxy_types: created.proxy_types || selectedTypes
        };
      } else {
        const id = this.selectedProvider ? this.selectedProvider.id : null;
        if (!id) {
          this.dialog.alert("No provider selected to update");
          this.saving = false;
          return;
        }

        const payload = {
          name: this.editingProviderData.name,
          description: this.editingProviderData.description,
          logo_url: this.editingProviderData.logo_url || null,
          website_url: this.editingProviderData.website_url || null,
          proxy_type: selectedTypes[0] || "residential",
          proxy_types: selectedTypes,
          status: this.editingProviderData.status || "pending",
          verified: !!this.editingProviderData.verified,
          real_cost_per_gb: parseFloat(this.editingProviderData.real_cost_per_gb) || 0.0,
          ip_pool_size: parseInt(this.editingProviderData.ip_pool_size, 10) || 0,
          asn_diversity_score: parseFloat(this.editingProviderData.asn_diversity_score) || 0.0,
          uptime_percentage: parseInt(this.editingProviderData.uptime_percentage, 10) || 0,
          benchmark_score: parseFloat(this.editingProviderData.benchmark_score) || 0.0,
          verified_block_rate: parseFloat(this.editingProviderData.verified_block_rate) || 0.0,
          avg_session_affinity_length: parseFloat(this.editingProviderData.avg_session_affinity_length) || 0.0,
          verification_tier: this.editingProviderData.verification_tier || "none",
          featured_weight: parseInt(this.editingProviderData.featured_weight, 10) || 0,
          featured_rank: parseInt(this.editingProviderData.featured_rank, 10) || 0,
          featured_until: this.editingProviderData.featured_until || null,
          provider_of_month: !!this.editingProviderData.provider_of_month,
          supported_protocols: protocols,
          country_codes: countries,
          use_cases: cases,
          pricing_tiers: pricingTiers,
          ping_endpoints: pingEndpoints,
          metadata: this.editingProviderData.metadata || {},
          profile_attributes: {
            id: this.editingProfileData.id,
            overview: this.editingProfileData.overview || "",
            what_works: this.editingProfileData.what_works || "",
            what_hurts: this.editingProfileData.what_hurts || "",
            setup_guide: this.editingProfileData.setup_guide || "",
            sidebar_cta_text: this.editingProfileData.sidebar_cta_text || "",
            affiliate_url: this.editingProfileData.affiliate_url || "",
            pros: pros,
            cons: cons,
            faq: faq
          }
        };
        const saveRes = await this.providerAdmin.saveProvider(id, payload);

        // Save per-type pricing (including pricing_unit) on edit
        if (this.providerTypePricings.length > 0) {
          try {
            await ajax(`/admin/plugins/proxyviews-directory/networks/${id}/pricings.json`, {
              type: "PUT",
              data: { pricings: this.providerTypePricings },
            });
          } catch {
            // Non-fatal — core provider save already succeeded.
          }
        }

        if (saveRes?.provider) {
          this.selectedProvider = { ...saveRes.provider };
          this.editingProviderData = {
            ...this.editingProviderData,
            ...saveRes.provider,
            proxy_types: saveRes.provider.proxy_types || selectedTypes
          };
        }
      }

      // Save succeeded — show success, refresh list, close modal
      this.formDirty = false;
      this.saveSuccess = true;
      this.fieldErrors = {};
      const savedName = this.editingProviderData.name;
      this.loadProviders();
      // Close the modal after a brief delay so the user sees the "Saved ✓"
      setTimeout(() => {
        this.saveSuccess = false;
        this.selectedProvider = null;
      }, 1200);
    } catch (err) {
      // Keep the modal open and show errors clearly
      this.fieldErrors = this.extractErrors(err);
      const errorMsg = Object.values(this.fieldErrors).flat().join(", ");
      this.dialog.alert(`Save failed: ${errorMsg}`);
    } finally {
      this.saving = false;
    }
  }

  @action
  deleteProvider(provider) {
    this.dialog.yesNoConfirm({
      message: `Are you sure you want to permanently delete '${provider.name}'?`,
      didConfirm: async () => {
        try {
          await this.providerAdmin.deleteProvider(provider.id);
          this.selectedProvider = null;
          this.showToast(`Provider '${provider.name}' deleted successfully!`);
          this.loadProviders();
        } catch (err) {
          popupAjaxError(err);
        }
      }
    });
  }

  @action
  seedProviders() {
    this.dialog.yesNoConfirm({
      message: "Are you sure you want to seed default sample providers? This will only seed non-existent ones.",
      didConfirm: async () => {
        try {
          await ajax("/admin/plugins/proxyviews-directory/seed", { type: "POST" });
          this.loadProviders();
        } catch (err) {
          popupAjaxError(err);
        }
      }
    });
  }

  @action
  toggleImportForm() {
    this.showImportForm = !this.showImportForm;
    this.importPreview = null;
    this.importErrors = [];
    this.importToken = null;
  }

  @action
  async handleImportUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    this.importing = true;
    try {
      const res = await ajax("/admin/plugins/proxyviews-directory/import.csv", {
        type: "POST",
        data: formData,
        cache: false,
        contentType: false,
        processData: false
      });

      this.importPreview = res.preview || [];
      this.importErrors = res.errors || [];
      this.importToken = res.token;
    } catch (err) {
      popupAjaxError(err);
    } finally {
      this.importing = false;
    }
  }

  @action
  async confirmImport() {
    if (!this.importToken) return;
    this.importing = true;

    try {
      const res = await ajax("/admin/plugins/proxyviews-directory/import.csv", {
        type: "POST",
        data: { confirm: "true", token: this.importToken }
      });
      this.dialog.alert(`Success! Imported ${res.created} and updated ${res.updated} providers.`);
      this.showImportForm = false;
      this.importPreview = null;
      this.importToken = null;
      this.loadProviders();
    } catch (err) {
      popupAjaxError(err);
    } finally {
      this.importing = false;
    }
  }

  <template>
    <div class="admin-plugin-proxyviews pd-admin-unified-crud">
      <!-- Curation Directory Dashboard Stats (click to filter) -->
      <div class="pd-admin-stats">
        <div class="pd-admin-stat-card pd-stat-clickable" {{on "click" (fn this.filterByStat "all")}}>
          <div class="stat-header"><span class="stat-label">Total Providers</span></div>
          <span class="stat-value">{{this.stats.total_providers}}</span>
        </div>
        <div class="pd-admin-stat-card pd-stat-clickable" {{on "click" (fn this.filterByStat "active")}}>
          <div class="stat-header"><span class="stat-label">Active Vendors</span></div>
          <span class="stat-value">{{this.stats.active_vendors}}</span>
        </div>
        <div class="pd-admin-stat-card pd-stat-clickable" {{on "click" (fn this.filterByStat "pending")}}>
          <div class="stat-header"><span class="stat-label">Pending Review</span></div>
          <span class="stat-value">{{this.stats.providers_needing_review}}</span>
        </div>
        <div class="pd-admin-stat-card">
          <div class="stat-header"><span class="stat-label">Average Rating</span></div>
          <span class="stat-value">★ {{this.stats.avg_rating}}</span>
        </div>
      </div>

      <!-- Split Pane Container -->
      {{#if this.successMessage}}
        <div class="alert alert-success pd-toast-banner" role="status" style="margin-bottom: 12px; display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-radius: 8px; font-weight: 600; background: rgba(46, 125, 50, 0.12); color: #2e7d32; border: 1px solid rgba(46, 125, 50, 0.3);">
          <span style="font-size: 1.2rem;">✓</span>
          <span>{{this.successMessage}}</span>
        </div>
      {{/if}}
      <div class="pd-split-pane {{if this.paneCollapsed "pane-collapsed"}}">
        <!-- Left Pane: List View -->
        <div class="pd-pane-left {{if this.isExpanded 'pd-expanded'}}">
          <!-- Collapse toggle (only visible when pane is collapsed) -->
          <button type="button" class="pd-pane-collapse-btn" {{on "click" this.togglePaneCollapse}} aria-label="Expand provider list" title="Expand list">
            ►
          </button>
          <div class="pd-pane-header" style="display: block; position: static; border-bottom: none; padding-bottom: 0;">
            <!-- Collapse button (visible when pane is expanded) -->
            <button type="button" class="btn btn-default btn-small pd-collapse-toggle" {{on "click" this.togglePaneCollapse}} title="Collapse list" style="margin-left: auto; padding: 4px 8px;">
              ◄
            </button>
            <!-- Action Panel & Filters -->
            <div class="pd-admin-filter-bar">
              <div class="search-input-wrapper">
                <input
                  value={{this.searchQuery}}
                  placeholder="Search by name or description..."
                  class="pd-admin-search-input"
                  {{on "input" this.updateSearch}}
                />
              </div>

              <select class="input-small" {{on "change" this.changeProxyTypeFilter}}>
                <option value="">All Types</option>
                <option value="residential">Residential</option>
                <option value="datacenter">Datacenter</option>
                <option value="mobile">Mobile</option>
                <option value="isp">ISP</option>
                <option value="rotating">Rotating</option>
              </select>

              <select class="input-small" {{on "change" this.changeStatusFilter}}>
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="archived">Archived</option>
              </select>

              <button type="button" class="btn btn-default btn-small" {{on "click" this.toggleNewForm}}>
                + New Provider
              </button>

              <button type="button" class="btn btn-default btn-small pd-view-toggle" title={{if (eq this.viewMode "table") "Switch to grid view" "Switch to table view"}} {{on "click" this.toggleViewMode}}>
                {{if (eq this.viewMode "table") "▦" "≡"}}
              </button>

              <button type="button" class="btn btn-default btn-small pd-expand-toggle {{if this.isExpanded 'active'}}" title={{if this.isExpanded "Normal view width" "Expand view width"}} {{on "click" this.toggleExpanded}}>
                {{if this.isExpanded "⛶ Normal" "⛶ Expand"}}
              </button>

              <button type="button" class="btn btn-default btn-small" {{on "click" this.seedProviders}}>
                Seed Sample Data
              </button>

              <a href="/admin/plugins/proxyviews-directory/export.csv" class="btn btn-default btn-small">Export CSV</a>

              <button type="button" class="btn btn-default btn-small" {{on "click" this.toggleImportForm}}>
                {{if this.showImportForm "✕ Cancel" "Import CSV"}}
              </button>
            </div>
          </div>

    <div class="pd-pane-body">
      <!-- CSV Import panel -->
      {{#if this.showImportForm}}
        <div class="pd-admin-new-provider-form">
          <input type="file" accept=".csv" {{on "change" this.handleImportUpload}} />
          {{#if this.importing}}
            <span class="pd-admin-search-status">Importing...</span>
          {{/if}}

          {{#if this.importPreview}}
            <div class="pd-admin-table-container" style="margin-top: 12px; width: 100%;">
              <h4>Import Preview</h4>
              <table class="pd-admin-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Name</th>
                    <th>Proxy Type</th>
                    <th>Price/GB</th>
                  </tr>
                </thead>
                <tbody>
                  {{#each this.importPreview as |row|}}
                    <tr>
                      <td><span class="pd-badge">{{row.action}}</span></td>
                      <td><strong>{{row.name}}</strong></td>
                      <td>{{row.proxy_type}}</td>
                      <td>${{row.real_cost_per_gb}}</td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
              {{#if this.importErrors.length}}
                <div class="alert alert-danger" style="margin-top: 8px;">
                  {{#each this.importErrors as |err|}}
                    <div>{{err}}</div>
                  {{/each}}
                </div>
              {{/if}}
              <div style="margin-top: 8px;">
                <button type="button" class="btn btn-primary btn-small" {{on "click" this.confirmImport}}>
                  Confirm Import
                </button>
              </div>
            </div>
          {{/if}}
        </div>
      {{/if}}

      <!-- New Provider Form moved to modal — triggered by the + New Provider button -->

      <!-- Bulk Actions Panel -->
      {{#if this.selectedProviderIds.length}}
        <div class="alert alert-info bulk-actions-panel" style="margin-top: 12px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
          <span><strong>{{this.selectedProviderIds.length}}</strong> providers selected. Bulk Actions:</span>
          <button type="button" class="btn btn-default btn-small" {{on "click" (fn this.applyBulkStatus "active")}}>Activate</button>
          <button type="button" class="btn btn-default btn-small" {{on "click" (fn this.applyBulkStatus "suspended")}}>Suspend</button>
          <button type="button" class="btn btn-default btn-small" {{on "click" (fn this.applyBulkVerified true)}}>Verify</button>
          <button type="button" class="btn btn-default btn-small" {{on "click" (fn this.applyBulkVerified false)}}>Unverify</button>
          <button type="button" class="btn btn-danger btn-small" {{on "click" this.bulkDelete}}>Delete Selected</button>
        </div>
      {{/if}}

      <!-- Provider List Table -->
      <div class="pd-admin-table-container" style="margin-top: 16px;">
        {{#if this.loading}}
          <div class="pd-admin-search-status">Loading providers list...</div>
        {{else if this.providers.length}}
          {{#if (eq this.viewMode "grid")}}
            <!-- Grid/Card View -->
            <div class="pd-provider-grid">
              {{#each this.providers as |p|}}
                <div class="pd-provider-card" role="button" {{on "click" (fn this.selectProvider p)}}>
                  <div class="pd-provider-card__header">
                    <div class="pd-provider-card__logo">
                      {{#if p.logo_url}}
                        <img src={{p.logo_url}} alt="" {{on "error" this.handleLogoError}} />
                      {{else}}
                        <span class="logo-placeholder">{{this.logoInitial p.name}}</span>
                      {{/if}}
                    </div>
                    <div class="pd-provider-card__info">
                      <span class="pd-provider-card__name">{{p.name}}</span>
                      <span class="pd-provider-card__slug">{{p.slug}}</span>
                    </div>
                    <span class="pd-status-badge pd-status--{{p.status}}">{{p.status}}</span>
                  </div>
                  <div class="pd-provider-card__pills">
                    {{#each (or p.proxy_types (array p.proxy_type)) as |t|}}
                      <span class="pd-type-pill pd-type-{{t}}">{{t}}</span>
                    {{/each}}
                  </div>
                  <div class="pd-provider-card__metrics">
                    <div class="pd-provider-card__metric">
                      <span class="pd-provider-card__metric-label">★ Rating</span>
                      <span class="pd-provider-card__metric-value">{{p.overall_rating}} ({{p.review_count}})</span>
                    </div>
                    <div class="pd-provider-card__metric">
                      <span class="pd-provider-card__metric-label">💵 Cost</span>
                      <span class="pd-provider-card__metric-value">${{or p.price_range_string p.real_cost_per_gb}}</span>
                    </div>
                  </div>
                  <div class="pd-provider-card__actions" {{on "click" this.stopRowClick}}>
                    <button type="button" class="btn btn-primary btn-small" {{on "click" (fn this.selectProvider p)}}>Edit</button>
                    <button type="button" class="btn btn-danger btn-small" {{on "click" (fn this.deleteProvider p)}}>Delete</button>
                  </div>
                </div>
              {{/each}}
            </div>
          {{else}}
          <table class="pd-admin-table">
            <colgroup>
              <col style="width:44px" />
              <col style="min-width:220px" />
              <col style="width:150px" />
              <col style="width:120px" />
              <col style="width:70px" />
              <col style="width:100px" />
              <col style="width:110px" />
              <col style="width:130px" />
            </colgroup>
            <thead>
              <tr>
                <th><input type="checkbox" checked={{this.allSelected}} {{on "change" this.toggleSelectAll}} aria-label="Select All" /></th>
                <th class="pd-sortable" {{on "click" (fn this.sortByColumn "name")}}>Name {{#if (eq this.sortBy "name")}}{{this.sortIndicator}}{{/if}}</th>
                <th>Proxy Type</th>
                <th class="pd-sortable" {{on "click" (fn this.sortByColumn "status")}}>Status {{#if (eq this.sortBy "status")}}{{this.sortIndicator}}{{/if}}</th>
                <th style="text-align:center">✓</th>
                <th class="pd-sortable" {{on "click" (fn this.sortByColumn "rating")}}>Rating {{#if (eq this.sortBy "rating")}}{{this.sortIndicator}}{{/if}}</th>
                <th class="pd-sortable" {{on "click" (fn this.sortByColumn "cost")}}>Cost/GB {{#if (eq this.sortBy "cost")}}{{this.sortIndicator}}{{/if}}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.providers as |p|}}
                <tr
                  class={{if (eq this.selectedProvider.id p.id) "active-row"}}
                  role="button"
                  {{on "click" (fn this.selectProvider p)}}
                >
                  <td {{on "click" this.stopRowClick}}>
                    <input
                      type="checkbox"
                      checked={{includes this.selectedProviderIds p.id}}
                      {{on "change" (fn this.toggleSelectProvider p.id)}}
                      aria-label="Select row"
                    />
                  </td>
                  <td>
                    <div class="provider-info">
                      <div class="provider-logo">
                        {{#if p.logo_url}}
                          <img
                            src={{p.logo_url}}
                            alt={{p.name}}
                            {{on "error" this.handleLogoError}}
                          />
                        {{else}}
                          <span class="logo-placeholder">{{this.logoInitial p.name}}</span>
                        {{/if}}
                      </div>
                      <div class="provider-text">
                        <span class="name">{{p.name}}</span>
                        <span class="slug">{{p.slug}}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="pd-type-pills">
                      {{#each (or p.proxy_types (array p.proxy_type)) as |t|}}
                        <span class="pd-type-pill pd-type-{{t}}">{{t}}</span>
                      {{/each}}
                    </div>
                  </td>
                  <td class="pd-inline-editable-cell" {{on "click" this.stopRowClick}}>
                    <select class="pd-status-select pd-status--{{p.status}}" disabled={{eq this.inlineSavingId p.id}} {{on "change" (fn this.saveInlineField p "status")}}>
                      <option value="pending" selected={{eq p.status "pending"}}>Pending</option>
                      <option value="active" selected={{eq p.status "active"}}>Active</option>
                      <option value="suspended" selected={{eq p.status "suspended"}}>Suspend</option>
                      <option value="archived" selected={{eq p.status "archived"}}>Archive</option>
                    </select>
                  </td>
                  <td class="pd-inline-editable-cell pd-verified-cell" {{on "click" this.stopRowClick}}>
                    <input type="checkbox" checked={{p.verified}} disabled={{eq this.inlineSavingId p.id}} {{on "change" (fn this.saveInlineField p "verified")}} />
                    {{#if p.verified}}<span class="pd-verified-check" title="Verified">✓</span>{{/if}}
                  </td>
                  <td class="pd-rating-cell">
                    <span class="pd-rating-star">★</span>
                    <span class="pd-rating-value">{{p.overall_rating}}</span>
                    <span class="pd-rating-count">({{p.review_count}})</span>
                  </td>
                  <td class="pd-cost-cell">${{or p.price_range_string p.real_cost_per_gb}}</td>
                  <td {{on "click" this.stopRowClick}}>
                    <div class="row-actions">
                      <button type="button" class="btn btn-primary btn-small" {{on "click" (fn this.selectProvider p)}}>
                        Edit
                      </button>
                      <button type="button" class="btn btn-danger btn-small" {{on "click" (fn this.deleteProvider p)}}>
                        Del
                      </button>
                    </div>
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
          {{/if}}
        {{else}}
          <div class="pd-empty-state">
            <div class="pd-empty-icon">📂</div>
            <h3 class="pd-empty-title">No providers found</h3>
            <p class="pd-empty-description">Try adjusting your filters or search term to find what you're looking for.</p>
          </div>
        {{/if}}
      </div>
      </div> <!-- End pd-pane-body -->

      <!-- Pagination -->
      <div class="pd-pane-footer">
      {{#unless (eq this.totalPages 1)}}
          <div class="pd-admin-pagination" style="margin: 0;">
            <button type="button" class="btn btn-default btn-small" disabled={{not this.hasPrevPage}} {{on "click" this.prevPage}}>
              &larr; Previous
            </button>
            <span class="pd-admin-pagination__status">
              Page {{this.page}} of {{this.totalPages}} ({{this.totalCount}} providers total)
            </span>
            <button type="button" class="btn btn-default btn-small" disabled={{not this.hasNextPage}} {{on "click" this.nextPage}}>
              Next &rarr;
            </button>
          </div>
      {{/unless}}
        </div> <!-- End pd-pane-footer -->
        </div> <!-- End pd-pane-left -->
      </div> <!-- End pd-split-pane -->

      <!-- Edit Modal: centered overlay — rendered OUTSIDE the split-pane so
           the overflow:hidden container doesn't clip the fixed-position modal -->
      {{#if this.selectedProvider}}
        <div class="pd-modal-overlay" {{on "click" this.handleOverlayClick}}>
          <div class="pd-edit-modal" {{on "click" this.stopRowClick}}>
          <div class="pd-pane-header pd-modal-header">
            <div class="pd-modal-header-left">
              <button type="button" class="btn btn-default btn-small pd-nav-btn" disabled={{this.canGoPrev}} title="Previous provider (↑)" {{on "click" this.prevProvider}}>←</button>
              <div class="pd-modal-provider-identity">
                <div class="pd-modal-logo">
                  {{#if this.selectedProvider.logo_url}}
                    <img src={{this.selectedProvider.logo_url}} alt="" {{on "error" this.handleLogoError}} />
                  {{else}}
                    <span class="logo-placeholder">{{this.logoInitial this.selectedProvider.name}}</span>
                  {{/if}}
                </div>
                <div class="pd-modal-title-group">
                  <h3>{{if this.selectedProvider.isNew "Add New Provider" this.selectedProvider.name}}</h3>
                  <div class="pd-modal-meta">
                    <span class="pd-modal-slug">{{this.selectedProvider.slug}}</span>
                    <span class="pd-modal-badge pd-status-badge pd-status--{{this.selectedProvider.status}}">{{this.selectedProvider.status}}</span>
                    {{#if this.selectedProvider.verified}}<span class="pd-modal-badge pd-verified-badge">✓ Verified</span>{{/if}}
                  </div>
                </div>
              </div>
              <button type="button" class="btn btn-default btn-small pd-nav-btn" disabled={{this.canGoNext}} title="Next provider (↓)" {{on "click" this.nextProvider}}>→</button>
            </div>
            <button type="button" class="pd-modal-close" {{on "click" this.closeDrawer}} aria-label="Close">&times;</button>
          </div>

          <div class="pd-drawer-tabs">
            <button type="button" class="tab {{if (eq this.activeEditTab 'core') 'active'}}" {{on "click" (fn this.changeEditTab 'core')}}>Core Info</button>
            <button type="button" class="tab {{if (eq this.activeEditTab 'pricing') 'active'}}" {{on "click" (fn this.changeEditTab 'pricing')}}>Pricing</button>
            <button type="button" class="tab {{if (eq this.activeEditTab 'performance') 'active'}}" {{on "click" (fn this.changeEditTab 'performance')}}>Performance</button>
            <button type="button" class="tab {{if (eq this.activeEditTab 'reviews') 'active'}}" {{on "click" (fn this.changeEditTab 'reviews')}}>Reviews & Mod</button>
            <button type="button" class="tab {{if (eq this.activeEditTab 'affiliate') 'active'}}" {{on "click" (fn this.changeEditTab 'affiliate')}}>Affiliate</button>
            <button type="button" class="tab {{if (eq this.activeEditTab 'analytics') 'active'}}" {{on "click" (fn this.changeEditTab 'analytics')}}>Analytics</button>
          </div>

          <div class="pd-pane-body">
            {{#if this.saveSuccess}}
              <div class="alert alert-success pd-modal-success-banner" role="status" style="margin-bottom: 16px; display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 8px; font-weight: 600; background: rgba(46, 125, 50, 0.12); color: #2e7d32; border: 1px solid rgba(46, 125, 50, 0.3);">
                <span style="font-size: 1.3rem;">✓</span>
                <div>
                  <div style="font-size: 0.95rem; font-weight: 700;">Provider Saved Successfully!</div>
                  <div style="font-size: 0.82rem; font-weight: 400; opacity: 0.9;">All proxy types, pricing overrides, profile details, and metadata have been updated.</div>
                </div>
              </div>
            {{/if}}
            <form {{on "submit" this.saveProvider}} {{on "input" this.markDirty}}>
              <!-- CORE INFO TAB -->
              {{#if (eq this.activeEditTab 'core')}}

                <!-- Section 1: Identity & URLs -->
                <div class="pd-accordion">
                  <button type="button" class="pd-accordion__header {{if this.coreSections.identity 'expanded'}}" {{on "click" (fn this.toggleCoreSection 'identity')}}>
                    {{if this.coreSections.identity "▼" "▶"}} Identity & URLs
                  </button>
                  {{#if this.coreSections.identity}}
                    <div class="pd-accordion__body">
                      <label class="pd-sidebar-label">Name</label>
                      <input type="text" value={{this.editingProviderData.name}} {{on "input" (fn this.updateEditField "name")}} class="input-large {{if this.fieldErrors.name 'pd-input-error'}}" />
                      {{#if this.fieldErrors.name}}<span class="pd-field-error">{{this.fieldErrors.name}}</span>{{/if}}

                      <label class="pd-sidebar-label">Website URL</label>
                      <input type="text" value={{this.editingProviderData.website_url}} {{on "input" (fn this.updateEditField "website_url")}} class="input-large {{if this.fieldErrors.website_url 'pd-input-error'}}" />
                      {{#if this.fieldErrors.website_url}}<span class="pd-field-error">{{this.fieldErrors.website_url}}</span>{{/if}}

                      <label class="pd-sidebar-label">Logo URL</label>
                      <input type="text" value={{this.editingProviderData.logo_url}} {{on "input" (fn this.updateEditField "logo_url")}} class="input-large {{if this.fieldErrors.logo_url 'pd-input-error'}}" />
                      {{#if this.fieldErrors.logo_url}}<span class="pd-field-error">{{this.fieldErrors.logo_url}}</span>{{/if}}

                      <label class="pd-sidebar-label">Short Description</label>
                      <textarea rows="3" value={{this.editingProviderData.description}} {{on "input" (fn this.updateEditField "description")}} class="pd-admin-textarea"></textarea>
                    </div>
                  {{/if}}
                </div>

                <!-- Section 2: Classification -->
                <div class="pd-accordion">
                  <button type="button" class="pd-accordion__header {{if this.coreSections.classification 'expanded'}}" {{on "click" (fn this.toggleCoreSection 'classification')}}>
                    {{if this.coreSections.classification "▼" "▶"}} Classification
                  </button>
                  {{#if this.coreSections.classification}}
                    <div class="pd-accordion__body">
                      <div class="pd-drawer-grid">
                        <div>
                          <label class="pd-sidebar-label">Proxy Types</label>
                          <div class="pd-type-checkboxes">
                            {{#each this.proxyTypeOptions as |ptype|}}
                              <label class="pd-checkbox-pill">
                                <input type="checkbox"
                                  checked={{includes this.editingProviderData.proxy_types ptype}}
                                  {{on "change" (fn this.toggleProxyType ptype)}} />
                                {{ptype}}
                              </label>
                            {{/each}}
                          </div>
                        </div>
                        <div>
                          <label class="pd-sidebar-label">Status</label>
                          <select class="input-medium" {{on "change" (fn this.updateEditField "status")}}>
                            <option value="pending" selected={{eq this.editingProviderData.status "pending"}}>Pending</option>
                            <option value="active" selected={{eq this.editingProviderData.status "active"}}>Active</option>
                            <option value="suspended" selected={{eq this.editingProviderData.status "suspended"}}>Suspended</option>
                            <option value="archived" selected={{eq this.editingProviderData.status "archived"}}>Archived</option>
                          </select>
                        </div>
                      </div>

                      <div class="pd-drawer-grid" style="margin-top: 12px;">
                        <label class="pd-sidebar-label">
                          <input type="checkbox" checked={{this.editingProviderData.verified}} {{on "change" (fn this.updateEditField "verified")}} />
                          Verified Listing
                        </label>
                        <label class="pd-sidebar-label">
                          <input type="checkbox" checked={{this.editingProviderData.provider_of_month}} {{on "change" (fn this.updateEditField "provider_of_month")}} />
                          Provider of Month
                        </label>
                      </div>

                      <label class="pd-sidebar-label">Supported Protocols (comma separated)</label>
                      <input type="text" value={{this.editingProviderData.supported_protocols}} {{on "input" (fn this.updateEditField "supported_protocols")}} placeholder="http, https, socks5" class="input-large" />

                      <label class="pd-sidebar-label">Country Codes (comma separated)</label>
                      <input type="text" value={{this.editingProviderData.country_codes}} {{on "input" (fn this.updateEditField "country_codes")}} placeholder="US, GB, FR, DE" class="input-large" />

                      <label class="pd-sidebar-label">Use Cases (comma separated)</label>
                      <input type="text" value={{this.editingProviderData.use_cases}} {{on "input" (fn this.updateEditField "use_cases")}} placeholder="scraping, ads, social" class="input-large" />
                    </div>
                  {{/if}}
                </div>

                <!-- Section 3: Featured Curation -->
                <div class="pd-accordion">
                  <button type="button" class="pd-accordion__header {{if this.coreSections.featured 'expanded'}}" {{on "click" (fn this.toggleCoreSection 'featured')}}>
                    {{if this.coreSections.featured "▼" "▶"}} Featured Curation
                  </button>
                  {{#if this.coreSections.featured}}
                    <div class="pd-accordion__body">
                      <div class="pd-drawer-grid">
                        <div>
                          <label class="pd-sidebar-label">Weight</label>
                          <input type="number" value={{this.editingProviderData.featured_weight}} {{on "input" (fn this.updateEditField "featured_weight")}} class="input-small" />
                        </div>
                        <div>
                          <label class="pd-sidebar-label">Rank</label>
                          <input type="number" value={{this.editingProviderData.featured_rank}} {{on "input" (fn this.updateEditField "featured_rank")}} class="input-small" />
                        </div>
                        <div>
                          <label class="pd-sidebar-label">Featured Until</label>
                          <input type="date" value={{this.formattedFeaturedUntil}} {{on "change" (fn this.updateEditField "featured_until")}} />
                        </div>
                      </div>
                    </div>
                {{/if}}
              </div>

                <!-- Section 4: Public Metadata -->
                <div class="pd-accordion">
                  <button type="button" class="pd-accordion__header {{if this.coreSections.metadata 'expanded'}}" {{on "click" (fn this.toggleCoreSection 'metadata')}}>
                    {{if this.coreSections.metadata "▼" "▶"}} Public Metadata
                  </button>
                  {{#if this.coreSections.metadata}}
                    <div class="pd-accordion__body">
                      <p class="description">Displayed on the public provider detail page as badges and sidebar info.</p>
                      <div class="pd-drawer-grid">
                        <div>
                          <label class="pd-sidebar-label">Free Trial</label>
                          <input type="text" value={{this.editingProviderData.metadata.free_trial}} {{on "input" (fn this.updateMetadataField "free_trial")}} placeholder="e.g. 7 days trial" class="input-medium" />
                        </div>
                        <div>
                          <label class="pd-sidebar-label">Speed</label>
                          <input type="text" value={{this.editingProviderData.metadata.speed}} {{on "input" (fn this.updateMetadataField "speed")}} placeholder="e.g. ⚡ Fast" class="input-medium" />
                        </div>
                      </div>
                      <div class="pd-drawer-grid" style="margin-top: 12px;">
                        <div>
                          <label class="pd-sidebar-label">Locations</label>
                          <input type="text" value={{this.editingProviderData.metadata.locations}} {{on "input" (fn this.updateMetadataField "locations")}} placeholder="e.g. 195 Countries" class="input-medium" />
                        </div>
                        <div>
                          <label class="pd-sidebar-label">Deal / Promo</label>
                          <input type="text" value={{this.editingProviderData.metadata.deal}} {{on "input" (fn this.updateMetadataField "deal")}} placeholder="e.g. 40% OFF" class="input-medium" />
                        </div>
                      </div>
                      <div class="pd-drawer-grid" style="margin-top: 12px;">
                        <div>
                          <label class="pd-sidebar-label">Coupon Code</label>
                          <input type="text" value={{this.editingProviderData.metadata.coupon_code}} {{on "input" (fn this.updateMetadataField "coupon_code")}} placeholder="e.g. SAVE40" class="input-medium" />
                        </div>
                        <div class="pd-payg-toggle-field">
                          <label class="pd-sidebar-label">Pay As You Go</label>
                          <label class="pd-payg-toggle">
                            <input type="checkbox" checked={{this.editingProviderData.metadata.pay_as_you_go}} {{on "change" (fn this.updateMetadataCheckbox "pay_as_you_go")}} />
                            <span class="pd-payg-toggle__track"><span class="pd-payg-toggle__thumb"></span></span>
                            <span class="pd-payg-toggle__label">{{if this.editingProviderData.metadata.pay_as_you_go "Enabled" "Disabled"}}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  {{/if}}
                </div>

                <!-- Section 5: Editorial Profile (collapsed by default — heaviest) -->
                <div class="pd-accordion">
                  <button type="button" class="pd-accordion__header {{if this.coreSections.editorial 'expanded'}}" {{on "click" (fn this.toggleCoreSection 'editorial')}}>
                    {{if this.coreSections.editorial "▼" "▶"}} Editorial Profile
                  </button>
                  {{#if this.coreSections.editorial}}
                    <div class="pd-accordion__body">
                      <label class="pd-sidebar-label">Overview Detail</label>
                      <textarea rows="4" value={{this.editingProfileData.overview}} {{on "input" (fn this.updateProfileField "overview")}} class="pd-admin-textarea"></textarea>

                      <label class="pd-sidebar-label">What Works</label>
                      <textarea rows="2" value={{this.editingProfileData.what_works}} {{on "input" (fn this.updateProfileField "what_works")}} class="pd-admin-textarea"></textarea>

                      <label class="pd-sidebar-label">What Hurts</label>
                      <textarea rows="2" value={{this.editingProfileData.what_hurts}} {{on "input" (fn this.updateProfileField "what_hurts")}} class="pd-admin-textarea"></textarea>

                      <label class="pd-sidebar-label">Pros (one per line)</label>
                      <textarea rows="3" value={{this.editingProfileData.pros}} {{on "input" (fn this.updateProfileField "pros")}} class="pd-admin-textarea" placeholder="Fast support&#10;Cheap starter pricing"></textarea>

                      <label class="pd-sidebar-label">Cons (one per line)</label>
                      <textarea rows="3" value={{this.editingProfileData.cons}} {{on "input" (fn this.updateProfileField "cons")}} class="pd-admin-textarea" placeholder="Hard configuration&#10;High payment surcharge"></textarea>

                      <label class="pd-sidebar-label">FAQ Array (JSON format)</label>
                      <textarea rows="4" value={{this.editingProfileData.faq_json}} {{on "input" (fn this.updateProfileField "faq_json")}} class="pd-admin-textarea pd-admin-textarea--mono"></textarea>
                    </div>
                  {{/if}}
                </div>

              {{/if}}

              <!-- PRICING TAB -->
              {{#if (eq this.activeEditTab 'pricing')}}
                <div class="pd-form-section">
                  <label class="pd-sidebar-label">Real Cost per GB ($)</label>
                  <input type="number" step="0.01" value={{this.editingProviderData.real_cost_per_gb}} {{on "input" (fn this.updateEditField "real_cost_per_gb")}} class="input-medium" />
                  <p class="description">Default price used when no per-type override exists below.</p>

                  <label class="pd-sidebar-label">Price per Proxy Type</label>
                  <p class="description">Set price and unit for each type. Datacenter/ISP are typically per-IP; residential/mobile are per-GB.</p>
                  <div class="pd-pricing-grid">
                    {{#each this.proxyTypeOptions as |ptype|}}
                      <div class="pd-pricing-row">
                        <span class="pd-pricing-type-label">{{ptype}}</span>
                        <input type="number" step="0.01"
                          value={{this.priceForType ptype}}
                          disabled={{not (includes this.editingProviderData.proxy_types ptype)}}
                          placeholder="default"
                          {{on "input" (fn this.updateTypePricing ptype)}} />
                        <select class="pd-pricing-unit-select" disabled={{not (includes this.editingProviderData.proxy_types ptype)}} {{on "change" (fn this.updatePricingUnit ptype)}}>
                          <option value="per_gb" selected={{or (eq this.pricingUnitForType ptype "per_gb") (not (includes this.editingProviderData.proxy_types ptype))}}>/GB</option>
                          <option value="per_ip" selected={{eq this.pricingUnitForType ptype "per_ip"}}>/IP</option>
                          <option value="flat" selected={{eq this.pricingUnitForType ptype "flat"}}>flat</option>
                        </select>
                      </div>
                    {{/each}}
                  </div>

                  <label class="pd-sidebar-label">Volume Pricing Tiers JSON</label>
                  <p class="description">JSON mapping for calculator volume discount bands (e.g. <code>{"starter": 3.0, "pro": 2.0}</code>)</p>
                  <KeyValueBuilder 
                    @value={{this.editingProviderData.pricing_tiers_json}} 
                    @onChange={{fn this.updateEditField "pricing_tiers_json"}} 
                    @keyPlaceholder="Tier (e.g. starter)"
                    @valPlaceholder="Cost (e.g. 3.0)"
                  />
                </div>
              {{/if}}

              <!-- PERFORMANCE TAB -->
              {{#if (eq this.activeEditTab 'performance')}}
                <div class="pd-form-section">
                  <div class="pd-drawer-grid">
                    <div>
                      <label class="pd-sidebar-label">Uptime (%)</label>
                      <input type="number" min="0" max="100" value={{this.editingProviderData.uptime_percentage}} {{on "input" (fn this.updateEditField "uptime_percentage")}} class="input-medium" />
                    </div>
                    <div>
                      <label class="pd-sidebar-label">Benchmark Score (0-100)</label>
                      <input type="number" step="0.1" value={{this.editingProviderData.benchmark_score}} {{on "input" (fn this.updateEditField "benchmark_score")}} class="input-medium" />
                    </div>
                  </div>

                  <div class="pd-drawer-grid" style="margin-top: 12px;">
                    <div>
                      <label class="pd-sidebar-label">Verified Block Rate (%)</label>
                      <input type="number" step="0.01" value={{this.editingProviderData.verified_block_rate}} {{on "input" (fn this.updateEditField "verified_block_rate")}} class="input-medium" />
                    </div>
                    <div>
                      <label class="pd-sidebar-label">ASN Diversity Score</label>
                      <input type="number" step="0.1" value={{this.editingProviderData.asn_diversity_score}} {{on "input" (fn this.updateEditField "asn_diversity_score")}} class="input-medium" />
                    </div>
                  </div>

                  <div class="pd-drawer-grid" style="margin-top: 12px;">
                    <div>
                      <label class="pd-sidebar-label">IP Pool Size</label>
                      <input type="number" value={{this.editingProviderData.ip_pool_size}} {{on "input" (fn this.updateEditField "ip_pool_size")}} class="input-medium" />
                    </div>
                    <div>
                      <label class="pd-sidebar-label">Session Affinity Length (sec)</label>
                      <input type="number" value={{this.editingProviderData.avg_session_affinity_length}} {{on "input" (fn this.updateEditField "avg_session_affinity_length")}} class="input-medium" />
                    </div>
                  </div>

                  <label class="pd-sidebar-label">Verification Tier</label>
                  <select class="input-medium" {{on "change" (fn this.updateEditField "verification_tier")}}>
                    <option value="none" selected={{eq this.editingProviderData.verification_tier "none"}}>None</option>
                    <option value="basic" selected={{eq this.editingProviderData.verification_tier "basic"}}>Basic</option>
                    <option value="benchmark" selected={{eq this.editingProviderData.verification_tier "benchmark"}}>Benchmark Verified</option>
                    <option value="premium" selected={{eq this.editingProviderData.verification_tier "premium"}}>Premium Verified</option>
                  </select>

                  <h4 style="margin: 24px 0 8px; border-bottom: 1px solid var(--primary-low); padding-bottom: 4px;">Live Benchmarking Config</h4>
                  <label class="pd-sidebar-label">Ping Endpoints JSON</label>
                  <p class="description">JSON mapping for live latency tests by region (e.g. <code>{"us-east": "https://us-east.provider.com/ping"}</code>). Used by the live browser-based benchmarking runner.</p>
                  <KeyValueBuilder 
                    @value={{this.editingProviderData.ping_endpoints_json}} 
                    @onChange={{fn this.updateEditField "ping_endpoints_json"}} 
                    @keyPlaceholder="Region (e.g. us-east)"
                    @valPlaceholder="Endpoint (e.g. https://...)"
                  />

                  {{#if this.selectedProviderSummary}}
                    <h4 style="margin: 24px 0 8px; border-bottom: 1px solid var(--primary-low); padding-bottom: 4px;">Live Performance Stats (Cross-Plugin)</h4>
                    <table class="pd-admin-table" style="margin:0;">
                      <tbody>
                        <tr>
                          <td><strong>Total Benchmark Runs Recorded</strong></td>
                          <td>{{this.selectedProviderSummary.benchmarks.total_runs}}</td>
                        </tr>
                        <tr>
                          <td><strong>Average Speed</strong></td>
                          <td>{{this.selectedProviderSummary.benchmarks.average_speed}} ms</td>
                        </tr>
                        <tr>
                          <td><strong>Average Uptime / Reliability</strong></td>
                          <td>{{this.selectedProviderSummary.benchmarks.average_reliability}}%</td>
                        </tr>
                        <tr>
                          <td><strong>Average Block Rate</strong></td>
                          <td>{{this.selectedProviderSummary.benchmarks.average_block_rate}}%</td>
                        </tr>
                      </tbody>
                    </table>
                  {{/if}}
                </div>
              {{/if}}

              <!-- REVIEWS & MODERATION TAB -->
              {{#if (eq this.activeEditTab 'reviews')}}
                <div class="pd-form-section">
                  <div class="pd-drawer-grid" style="align-items: center; margin-bottom: 12px;">
                    <label class="pd-sidebar-label" style="margin:0;">Moderation Queue Status:</label>
                    <select class="input-medium" {{on "change" this.changeReviewsQueue}}>
                      <option value="all" selected={{eq this.reviewsQueue "all"}}>All Reviews</option>
                      <option value="pending" selected={{eq this.reviewsQueue "pending"}}>Pending Review</option>
                      <option value="published" selected={{eq this.reviewsQueue "published"}}>Published Reviews</option>
                      <option value="reported" selected={{eq this.reviewsQueue "reported"}}>Reported Reviews</option>
                      <option value="hidden" selected={{eq this.reviewsQueue "hidden"}}>Hidden Reviews</option>
                    </select>
                  </div>

                  {{#if this.reviewsLoading}}
                    <div class="pd-admin-search-status">Loading reviews queue...</div>
                  {{else}}
                    {{#each this.providerReviews as |rev|}}
                      <div class="alert alert-info review-moderation-card" style="margin-bottom: 12px; background: var(--pd-surface); color: var(--pd-ink); border: 1px solid var(--pd-line); padding: 12px; border-radius: 8px;">
                        <div style="display:flex; justify-content:space-between; align-items:center;">
                          <strong>{{rev.title}}</strong>
                          <span>Rating: ★{{rev.overall_score}}</span>
                        </div>
                        <p style="margin: 6px 0; font-size: 0.9em; line-height: 1.4;">
                          <strong>Works:</strong> {{rev.what_works}}<br/>
                          <strong>Hurts:</strong> {{rev.what_hurts}}
                        </p>
                        <div style="font-size: 0.85em; color: var(--pd-muted); margin-bottom: 8px;">
                          By {{rev.user.username}} • {{rev.created_at}} •
                          {{#if rev.verified_purchase}}
                            <span class="pd-badge success" style="display: inline-block;">✓ Verified Purchase</span>
                          {{else}}
                            <span class="pd-badge" style="display: inline-block;">Not Verified</span>
                          {{/if}}
                        </div>
                        <div style="display: flex; gap: 8px;">
                          {{#unless (eq rev.status "published")}}
                            <button type="button" class="btn btn-primary btn-small" {{on "click" (fn this.moderateReview rev.id "approve")}}>Approve</button>
                          {{/unless}}
                          {{#unless (eq rev.status "hidden")}}
                            <button type="button" class="btn btn-danger btn-small" {{on "click" (fn this.moderateReview rev.id "hide")}}>Hide / Reject</button>
                          {{/unless}}
                          <button type="button" class="btn btn-default btn-small" {{on "click" (fn this.toggleReviewVerifiedPurchase rev.id rev.verified_purchase)}}>
                            Toggle Verified Purchase
                          </button>
                        </div>
                      </div>
                    {{else}}
                      <p class="description">No reviews found in this queue for {{this.selectedProvider.name}}.</p>
                    {{/each}}
                  {{/if}}
                </div>
              {{/if}}

              <!-- AFFILIATE TAB -->
              {{#if (eq this.activeEditTab 'affiliate')}}
                <div class="pd-form-section">
                  <label class="pd-sidebar-label">Affiliate / Website CTA Text</label>
                  <input type="text" value={{this.editingProfileData.sidebar_cta_text}} {{on "input" (fn this.updateProfileField "sidebar_cta_text")}} placeholder="Visit Website" class="input-large" />

                  <label class="pd-sidebar-label">Affiliate referral link URL</label>
                  <input type="text" value={{this.editingProfileData.affiliate_url}} {{on "input" (fn this.updateProfileField "affiliate_url")}} placeholder="https://..." class="input-large" />

                  {{#if this.selectedProviderSummary}}
                    <h4 style="margin: 24px 0 8px; border-bottom: 1px solid var(--primary-low); padding-bottom: 4px;">Affiliate Click Statistics (Cross-Plugin)</h4>
                    <table class="pd-admin-table" style="margin:0;">
                      <tbody>
                        <tr>
                          <td><strong>Total Referral Clicks</strong></td>
                          <td>{{this.selectedProviderSummary.affiliate.total_clicks}} clicks</td>
                        </tr>
                        <tr>
                          <td><strong>Clicks (Last 30 Days)</strong></td>
                          <td>{{this.selectedProviderSummary.affiliate.clicks_30_days}} clicks</td>
                        </tr>
                      </tbody>
                    </table>
                  {{/if}}
                </div>
              {{/if}}

              <!-- ANALYTICS TAB -->
              {{#if (eq this.activeEditTab 'analytics')}}
                <div class="pd-form-section">
                  {{#if this.summaryLoading}}
                    <div class="pd-admin-search-status">Loading analytics statistics...</div>
                  {{else if this.selectedProviderSummary}}
                    <h4 style="margin:0 0 12px; border-bottom: 1px solid var(--primary-low); padding-bottom: 4px;">Provider Detail View Stats (Cross-Plugin)</h4>
                    <div class="pd-admin-stats" style="grid-template-columns: 1fr;">
                      <div class="pd-admin-stat-card">
                        <div class="stat-header">
                          <span class="label">Total Directory Views (Slug-Matched Pageviews)</span>
                        </div>
                        <span class="value">{{this.selectedProviderSummary.analytics.page_views}} views</span>
                      </div>
                    </div>
                  {{else}}
                    <p class="description">Analytics statistics not available.</p>
                  {{/if}}
                </div>
              {{/if}}

            </form>
          </div> <!-- End pd-pane-body -->

          <!-- Modal Footer: pinned at bottom (outside body so it doesn't scroll) -->
          <div class="pd-pane-footer pd-modal-footer">
            <div class="pd-footer-status">
              {{#if this.saving}}
                <span class="pd-footer-indicator pd-footer-saving">⟳ Saving…</span>
              {{else if this.saveSuccess}}
                <span class="pd-footer-indicator pd-footer-success">✓ Saved</span>
              {{else if this.formDirty}}
                <span class="pd-footer-indicator pd-footer-dirty">● Unsaved changes</span>
              {{else}}
                <span class="pd-footer-position">Provider {{this.currentIndex}} of {{this.providers.length}}</span>
              {{/if}}
            </div>
            <div class="pd-footer-actions">
              <button type="button" class="btn btn-default btn-large" disabled={{this.saving}} {{on "click" this.closeDrawer}}>
                Close
              </button>
              <button type="button" class="btn btn-primary btn-large {{if this.saving "disabled"}}" disabled={{this.saving}} {{on "click" this.submitForm}}>
                {{if this.saving "Saving…" "Save Changes"}}
              </button>
            </div>
          </div>
          </div> <!-- End pd-edit-modal -->
        </div> <!-- End pd-modal-overlay -->
      {{/if}}

      <!-- Create Provider Modal (multi-step) -->
      {{#if this.showCreateModal}}
        <div class="pd-modal-overlay" {{on "click" this.closeCreateModal}}>
          <div class="pd-edit-modal" {{on "click" this.stopRowClick}} style="max-width: min(90vw, 640px);">
            <div class="pd-pane-header pd-modal-header">
              <div class="pd-modal-header-left">
                <h3>New Provider (Step {{this.createStep}} of 2)</h3>
              </div>
              <button type="button" class="pd-modal-close" {{on "click" this.closeCreateModal}}>&times;</button>
            </div>

            <div class="pd-pane-body">
              <form {{on "submit" this.createProvider}}>
                {{#if (eq this.createStep 1)}}
                  <label class="pd-sidebar-label">Provider Name *</label>
                  <input type="text" value={{this.createData.name}} placeholder="e.g. Acme Proxies" class="input-large {{if this.fieldErrors.name 'pd-input-error'}}" {{on "input" (fn this.updateCreateField "name")}} />
                  {{#if this.fieldErrors.name}}<span class="pd-field-error">{{this.fieldErrors.name}}</span>{{/if}}

                  <label class="pd-sidebar-label">Website URL</label>
                  <input type="text" value={{this.createData.website_url}} placeholder="https://example.com" class="input-large" {{on "input" (fn this.updateCreateField "website_url")}} />

                  <label class="pd-sidebar-label">Logo URL</label>
                  <input type="text" value={{this.createData.logo_url}} placeholder="https://example.com/logo.png" class="input-large" {{on "input" (fn this.updateCreateField "logo_url")}} />

                  <label class="pd-sidebar-label">Description</label>
                  <textarea rows="2" value={{this.createData.description}} placeholder="Short description..." class="pd-admin-textarea" {{on "input" (fn this.updateCreateField "description")}}></textarea>

                  <label class="pd-sidebar-label">Proxy Types</label>
                  <div class="pd-type-checkboxes">
                    {{#each this.proxyTypeOptions as |ptype|}}
                      <label class="pd-checkbox-pill">
                        <input type="checkbox" checked={{includes this.createData.proxy_types ptype}} {{on "change" (fn this.toggleCreateType ptype)}} />
                        {{ptype}}
                      </label>
                    {{/each}}
                  </div>

                  <div class="pd-drawer-grid">
                    <div>
                      <label class="pd-sidebar-label">Status</label>
                      <select class="input-medium" {{on "change" (fn this.updateCreateField "status")}}>
                        <option value="pending" selected={{eq this.createData.status "pending"}}>Pending</option>
                        <option value="active" selected={{eq this.createData.status "active"}}>Active</option>
                        <option value="suspended" selected={{eq this.createData.status "suspended"}}>Suspended</option>
                      </select>
                    </div>
                    <div>
                      <label class="pd-sidebar-label">
                        <input type="checkbox" checked={{this.createData.verified}} {{on "change" (fn this.updateCreateField "verified")}} />
                        Verified
                      </label>
                    </div>
                  </div>
                {{else}}
                  <p class="description">Optional — fill in performance metrics now or skip to create with defaults.</p>

                  <div class="pd-drawer-grid">
                    <div>
                      <label class="pd-sidebar-label">Cost per GB ($)</label>
                      <input type="number" step="0.01" value={{this.createData.real_cost_per_gb}} placeholder="0.00" class="input-medium" {{on "input" (fn this.updateCreateField "real_cost_per_gb")}} />
                    </div>
                    <div>
                      <label class="pd-sidebar-label">Uptime (%)</label>
                      <input type="number" min="0" max="100" value={{this.createData.uptime_percentage}} placeholder="99.5" class="input-medium" {{on "input" (fn this.updateCreateField "uptime_percentage")}} />
                    </div>
                  </div>
                  <div class="pd-drawer-grid">
                    <div>
                      <label class="pd-sidebar-label">IP Pool Size</label>
                      <input type="number" value={{this.createData.ip_pool_size}} placeholder="10000000" class="input-medium" {{on "input" (fn this.updateCreateField "ip_pool_size")}} />
                    </div>
                    <div>
                      <label class="pd-sidebar-label">Benchmark Score (0-100)</label>
                      <input type="number" step="0.1" value={{this.createData.benchmark_score}} placeholder="75.0" class="input-medium" {{on "input" (fn this.updateCreateField "benchmark_score")}} />
                    </div>
                  </div>

                  <label class="pd-sidebar-label" style="margin-top: 16px;">Price per Proxy Type ($/GB)</label>
                  <p class="description">Override the default cost for each type. Leave blank to use the default above.</p>
                  <div class="pd-pricing-grid">
                    {{#each this.createData.proxy_types as |ptype|}}
                      <div class="pd-pricing-row">
                        <span class="pd-pricing-type-label">{{ptype}}</span>
                        <input type="number" step="0.01"
                          value={{this.createPriceForType ptype}}
                          placeholder={{or this.createData.real_cost_per_gb "default"}}
                          {{on "input" (fn this.updateCreateTypePricing ptype)}} />
                      </div>
                    {{else}}
                      <p class="description">Select proxy types in Step 1 to set per-type pricing.</p>
                    {{/each}}
                  </div>
                {{/if}}
              </form>
            </div>

            <div class="pd-pane-footer pd-modal-footer">
              <div class="pd-footer-status">
                {{#if (eq this.createStep 1)}}
                  <span class="pd-footer-position">Step 1: Essential Information</span>
                {{else}}
                  <span class="pd-footer-position">Step 2: Pricing, Metrics & Per-Type Overrides (optional)</span>
                {{/if}}
              </div>
              <div class="pd-footer-actions">
                {{#if (eq this.createStep 2)}}
                  <button type="button" class="btn btn-default btn-large" {{on "click" this.prevCreateStep}}>Back</button>
                {{/if}}
                {{#if (eq this.createStep 1)}}
                  <button type="button" class="btn btn-default btn-large" {{on "click" this.closeCreateModal}}>Cancel</button>
                  <button type="button" class="btn btn-primary btn-large" {{on "click" this.nextCreateStep}}>Next →</button>
                {{else}}
                  <button type="button" class="btn btn-default btn-large" {{on "click" this.createProvider}}>Skip & Create</button>
                  <button type="button" class="btn btn-primary btn-large {{if this.saving "disabled"}}" disabled={{this.saving}} {{on "click" this.createProvider}}>
                    {{if this.saving "Creating..." "Create Provider"}}
                  </button>
                {{/if}}
              </div>
            </div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
